/**
 * useAudit — fires the Resales-Online audit beacons for the offer viewer,
 * mirroring the production property-viewer (ListProperties.js / PropertyDetails.js).
 *
 * The endpoint set depends on the link's uuid format, exactly as production does:
 *   • "RP-…" (rv3) uuids → the v3 endpoints (request.v3Post):
 *       /audit/searches, /audit/impressions, /audit/public/property-actions.
 *   • short / non-RP (gateway) uuids → the gateway-audit endpoints (request.post):
 *       /gateway-audit/search, /gateway-audit/impression, /gateway-audit/action.
 *
 * In both cases: searches + impressions fire once on the first load of a link;
 * a property "action" (VIEW) fires only when a property DETAIL is viewed:
 *   • cards layout: a card / prev / next opens the detail page;
 *   • list layout: the section the user scrolls to (scroll-spy / tab / deep link);
 *   • a single-property link is its own detail (fires on load).
 * Never on the cards results grid. Each actioned RsId is stored under
 * `audit_<uuid>` so re-viewing the same property never re-counts it.
 *
 * The "once per link" + per-property dedup use the SAME localStorage key the
 * production app uses — `audit_<uuid>` → JSON array of already-actioned RsIds —
 * so this viewer and the live app never double-count each other. Only loaded
 * links are audited (status 'live' with a uuid + searchInfo).
 */
import { useEffect } from 'react';
import request from '../../agent';
import configApis from '../../configs/apis';
import common from '../../constants/common';

// Same split production uses: long "RP-"-prefixed uuids are rv3 links; everything
// else goes through the gateway audit endpoints.
function isRpUuid(uuid) {
    return typeof uuid === 'string' && uuid.length > 11 && uuid.startsWith('RP-');
}

// Gateway audit SearchVersion is derived from the feed's search engine
// (CoreApi → 2.1, else 2.0), mirroring ListProperties / PropertyDetails.
function gatewaySearchVersion(searchInfo) {
    return searchInfo.SearchEngine === common.SEARCH_ENGINE_CORE_API
        ? common.SEARCH_VERSION_21
        : common.SEARCH_VERSION_20;
}

// Reads the production audit list for a link. Returns null when the key is
// absent (link not yet audited), otherwise the parsed array of RsIds.
function readAuditList(uuid) {
    const raw = localStorage.getItem('audit_' + uuid);
    if (raw === null) return null;
    try {
        return JSON.parse(raw) || [];
    } catch (e) {
        return [];
    }
}

// `detailIndex` is the index of the offer whose detail is currently viewed
// (opened in cards mode / scrolled to in list mode), or null when nothing is
// viewed yet (cards results grid, list page before any section is reached).
export default function useAudit({ status, uuid, agency, offers, searchInfo, detailIndex }) {
    // Only real, loaded links are audited.
    const live = status === 'live' && !!uuid;

    // --- page load: searches + impressions, once per link -------------------
    useEffect(() => {
        if (!live || !searchInfo || !offers.length) return;
        // getItem === null means this link has never been audited. Setting it to
        // '[]' below marks searches/impressions done (with no property-actions
        // yet), matching the production localStorage contract.
        if (localStorage.getItem('audit_' + uuid) !== null) return;

        // Sibling unit offers share their parent's rsId; audit each parent once.
        const rsids = offers.map((o) => o.rsId).filter((id, i, all) => all.indexOf(id) === i);

        if (isRpUuid(uuid)) {
            // rv3 (R+) audit
            request.v3Post(configApis.rv3_url + '/audit/searches', {
                systemType: common.AUDIT_SYSTEM_TYPE_PROPERTY_VIEWER,
                agencyId: agency.contactSecureId,
                searchType: searchInfo.SearchType,
                dimension: searchInfo.Dimension,
                currency: searchInfo.Currency,
                searchVersion: common.SEARCH_VERSION_30,
                refId: rsids.join(','),
            });
            request.v3Post(configApis.rv3_url + '/audit/impressions', {
                agencyId: agency.contactSecureId,
                systemType: common.AUDIT_SYSTEM_TYPE_PROPERTY_VIEWER,
                searchType: searchInfo.SearchType,
                searchVersion: common.SEARCH_VERSION_30,
                impressionType: common.AUDIT_IMPRESSION_TYPE_PROPERTY_VIEWER,
                rsIds: rsids,
                page: 1,
            });
        } else {
            // gateway audit — search + impression share one payload
            const auditData = {
                RsIds: rsids,
                SearchType: searchInfo.SearchType,
                SearchVersion: gatewaySearchVersion(searchInfo),
                ContactSecureId: agency.contactSecureId,
                AccountType: agency.accountType,
                NewDevAccess: agency.newDevAccess,
                Dimension: searchInfo.Dimension,
                Currency: searchInfo.Currency,
            };
            request.post(configApis.audit_url + '/gateway-audit/impression', auditData);
            request.post(configApis.audit_url + '/gateway-audit/search', auditData);
        }

        localStorage.setItem('audit_' + uuid, JSON.stringify([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [live, uuid, searchInfo, offers.length]);

    // --- detail: a property "action" (VIEW) each time a property detail opens.
    // Keyed off detailIndex (null → nothing viewed yet → no call). Deduped
    // per RsId via localStorage so re-opening the same property doesn't re-count.
    useEffect(() => {
        if (!live || !searchInfo || detailIndex == null) return;
        const offer = offers[detailIndex];
        if (!offer || offer.rsId == null) return;

        // null (searches effect not yet run) is treated as an empty list; the
        // searches effect runs first in the same commit, so this is just a guard.
        const audited = readAuditList(uuid) || [];
        if (audited.indexOf(offer.rsId) !== -1) return;

        if (isRpUuid(uuid)) {
            // SearchType 1 ("All") is split into development vs resale, as
            // production does from RsDevId (here: the mapped isDevelopment flag).
            let searchType = searchInfo.SearchType;
            if (searchType === 1) {
                searchType = offer.isDevelopment ? common.SEARCH_TYPE_DEVELOPMENT : common.SEARCH_TYPE_RESALE;
            }
            request.v3Post(configApis.rv3_url + '/audit/public/property-actions', {
                index: common.AUDIT_ACTION_TYPE_VIEW,
                systemType: common.AUDIT_SYSTEM_TYPE_PROPERTY_VIEWER,
                rsids: offer.rsId,
                searchType,
                agencyId: agency.contactSecureId,
            });
        } else {
            request.post(configApis.audit_url + '/gateway-audit/action', {
                RsId: offer.rsId,
                SearchType: searchInfo.SearchType,
                SearchVersion: gatewaySearchVersion(searchInfo),
                ContactSecureId: agency.contactSecureId,
            });
        }

        audited.push(offer.rsId);
        localStorage.setItem('audit_' + uuid, JSON.stringify(audited));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [live, detailIndex, offers, searchInfo]);
}
